import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useJsApiLoader, GoogleMap, Marker } from '@react-google-maps/api';

const libraries: ('places')[] = ['places'];

interface AddressMapModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (address: string, placeDetails?: any) => void;
  initialAddress?: string;
}

const defaultCenter = {
  lat: 40.6084, // Default to Brooklyn, NY (Flatbush Ave area)
  lng: -73.9232,
};

const defaultInitialAddress = '2126 Flatbush Ave, Brooklyn, NY 11234';

// Helper function to check if address is in USA
const isAddressInUSA = (place: any): boolean => {
  if (!place || !place.address_components) return false;
  return place.address_components.some(
    (component: any) =>
      component.types.includes('country') && component.short_name === 'US'
  );
};

// Helper function to remove country name from address string
const removeCountryFromAddress = (address: string): string => {
  if (!address) return address;
  // Remove common country suffixes: ", USA", ", United States", ", US"
  return address
    .replace(/,\s*USA\s*$/i, '')
    .replace(/,\s*United States\s*$/i, '')
    .replace(/,\s*US\s*$/i, '')
    .trim();
};

export default function AddressMapModal({
  open,
  onClose,
  onConfirm,
  initialAddress = defaultInitialAddress,
}: AddressMapModalProps) {
  const [selectedAddress, setSelectedAddress] = useState(initialAddress);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mapRef = useRef<google.maps.Map | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries,
  });

  // Geocode initial address when modal opens
  useEffect(() => {
    if (open) {
      const addressToSearch = initialAddress || defaultInitialAddress;
      setSelectedAddress(addressToSearch);
      setSearchValue(addressToSearch);
      
      // Reset marker position initially
      setMarkerPosition(null);
      setMapCenter(defaultCenter);
    }
  }, [open, initialAddress]);


  useEffect(() => {
    if (!isLoaded || !open || !searchInputRef.current || !window.google?.maps?.places) {
      return;
    }

    // Clean up existing autocomplete if any
    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }

    // Initialize Autocomplete for search - restricted to USA only
    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      searchInputRef.current,
      {
        types: ['address'],
        fields: ['formatted_address', 'address_components', 'geometry', 'place_id'],
        componentRestrictions: { country: 'us' },
      }
    );

    const handlePlaceChanged = () => {
      const place = autocompleteRef.current?.getPlace();
      if (place && place.geometry) {
        // Validate that the place is in USA
        if (!isAddressInUSA(place)) {
          setError('Please select an address in the United States.');
          setSelectedAddress('');
          setSelectedPlace(null);
          return;
        }
        
        const location = place.geometry.location;
        if (location) {
          const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
          const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
          
          const newCenter: google.maps.LatLngLiteral = { lat: Number(lat), lng: Number(lng) };
          setMapCenter(newCenter);
          setMarkerPosition(newCenter);
          const fullAddress = place.formatted_address || '';
          setSelectedAddress(fullAddress);
          setSelectedPlace(place);
          setSearchValue(removeCountryFromAddress(fullAddress));
          setError(null);
          
          // Pan map to the selected location
          if (mapRef.current) {
            mapRef.current.panTo(newCenter);
          }
        }
      }
    };

    autocompleteRef.current.addListener('place_changed', handlePlaceChanged);

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isLoaded, open]);

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = typeof e.latLng.lat === 'function' ? e.latLng.lat() : e.latLng.lat;
      const lng = typeof e.latLng.lng === 'function' ? e.latLng.lng() : e.latLng.lng;
      
      const position: google.maps.LatLngLiteral = { lat: Number(lat), lng: Number(lng) };
      setMarkerPosition(position);
      setMapCenter(position);

      // Reverse geocode to get address
      if (window.google?.maps) {
        setIsLoading(true);
        setError(null);
        const geocoder = new window.google.maps.Geocoder();
        
        geocoder.geocode({ 
          location: new window.google.maps.LatLng(position.lat, position.lng)
        }, (results, status) => {
          setIsLoading(false);
          if (status === 'OK' && results && results.length > 0) {
            // Filter results to only USA addresses
            const usaResults = results.filter(r => isAddressInUSA(r));
            
            if (usaResults.length === 0) {
              setError('This location is not in the United States. Please select a location within the USA.');
              setSelectedAddress('');
              setSelectedPlace(null);
              return;
            }
            
            // Try to find a result with a street address first
            let addressResult = usaResults.find(r => 
              r.types.includes('street_address') || 
              r.types.includes('premise') ||
              r.types.includes('subpremise')
            );
            
            // If no street address, try route (street name)
            if (!addressResult) {
              addressResult = usaResults.find(r => r.types.includes('route'));
            }
            
            // If still no good result, use the first USA result
            if (!addressResult) {
              addressResult = usaResults[0];
            }
            
            const formattedAddress = addressResult.formatted_address;
            
            // Validate that we have a proper address (not just coordinates or empty)
            if (formattedAddress && 
                formattedAddress.trim() !== '' && 
                !formattedAddress.match(/^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*\s*$/)) {
              setSelectedAddress(formattedAddress);
              setSelectedPlace(addressResult);
              setSearchValue(removeCountryFromAddress(formattedAddress));
              setError(null);
            } else {
              setError('Could not find a valid street address for this location. Please use the search box to find an address.');
              setSelectedAddress('');
              setSelectedPlace(null);
            }
          } else {
            setError('Could not find an address for this location. Please use the search box to find an address in the USA.');
            setSelectedAddress('');
            setSelectedPlace(null);
          }
        });
      }
    }
  };

  const handleConfirm = () => {
    // Only confirm if we have a valid address (not coordinates)
    if (selectedAddress && !selectedAddress.match(/^-?\d+\.?\d*,\s*-?\d+\.?\d*$/)) {
      onConfirm(selectedAddress, selectedPlace);
      onClose();
    } else {
      setError('Please select a valid address. Coordinates are not allowed.');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  // Function to find address on map from search input
  const findAddressOnMap = (address: string) => {
    if (!address || !address.trim() || !isLoaded || !window.google?.maps) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address: address.trim() },
      (results, status) => {
        setIsLoading(false);
        
        if (status === 'OK' && results && results.length > 0) {
          // Filter to only USA results
          const usaResults = results.filter(r => isAddressInUSA(r));
          
          if (usaResults.length > 0) {
            // Find the best result (prefer street_address, then premise, then route)
            let bestResult = usaResults.find(r => 
              r.types.includes('street_address') || 
              r.types.includes('premise') ||
              r.types.includes('subpremise')
            );
            
            if (!bestResult) {
              bestResult = usaResults.find(r => r.types.includes('route'));
            }
            
            if (!bestResult) {
              bestResult = usaResults[0];
            }
            
            const location = bestResult.geometry.location;
            const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
            const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
            const center: google.maps.LatLngLiteral = { lat: Number(lat), lng: Number(lng) };
            
            setMapCenter(center);
            setMarkerPosition(center);
            setSelectedPlace(bestResult);
            const fullAddress = bestResult.formatted_address || address.trim();
            setSelectedAddress(fullAddress);
            setSearchValue(removeCountryFromAddress(fullAddress));
            setError(null);
            
            // Pan and zoom map to the location
            if (mapRef.current) {
              mapRef.current.panTo(center);
              mapRef.current.setZoom(17); // Zoom in closer for exact address
            }
          } else {
            setError('Address not found in the United States. Please enter a valid USA address.');
            setSelectedAddress('');
            setSelectedPlace(null);
          }
        } else if (status === 'ZERO_RESULTS') {
          setError('No results found for this address. Please try a different address.');
          setSelectedAddress('');
          setSelectedPlace(null);
        } else {
          setError('Could not find the address. Please check the address and try again.');
          setSelectedAddress('');
          setSelectedPlace(null);
        }
      }
    );
  };

  const handleSearchSubmit = () => {
    if (searchValue.trim()) {
      findAddressOnMap(searchValue);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      e.preventDefault();
      findAddressOnMap(searchValue);
    }
  };

  if (loadError) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Select Address</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            Failed to load Google Maps. Please check your API key configuration.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Address on Map</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {!apiKey && (
            <Alert severity="warning">
              Google Maps API key not configured. Please set VITE_GOOGLE_MAPS_API_KEY in your .env file.
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              inputRef={searchInputRef}
              label="Search Address"
              value={searchValue}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              placeholder="Type an address to search..."
              fullWidth
              disabled={!isLoaded || isLoading}
              InputProps={{
                endAdornment: isLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSearchSubmit}
                      disabled={!isLoaded || isLoading || !searchValue.trim()}
                      edge="end"
                      aria-label="search address"
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {error && (
            <Alert severity="warning" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {selectedAddress && (
            <Alert severity="info">
              Selected: {selectedAddress}
            </Alert>
          )}

          <Box
            sx={{
              width: '100%',
              height: '500px', // Increased height for better visibility
              borderRadius: 2, // More rounded corners
              overflow: 'hidden',
              border: '2px solid',
              borderColor: 'primary.main',
              boxShadow: 3, // Add shadow for depth
              position: 'relative',
              '&:hover': {
                boxShadow: 6, // Enhanced shadow on hover
              },
            }}
          >
            {!isLoaded ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={mapCenter}
                zoom={markerPosition ? 15 : 10}
                onClick={handleMapClick}
                onLoad={(map) => {
                  mapRef.current = map;
                  // Search initial address when map is loaded
                  if (open) {
                    const addressToSearch = initialAddress || defaultInitialAddress;
                    setTimeout(() => {
                      findAddressOnMap(addressToSearch);
                    }, 100);
                  }
                }}
                options={{
                  disableDefaultUI: false,
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: false,
                  restriction: {
                    latLngBounds: {
                      north: 49.38,
                      south: 24.52,
                      east: -66.95,
                      west: -125.0,
                    },
                    strictBounds: false,
                  },
                }}
              >
                {markerPosition && <Marker position={markerPosition} />}
              </GoogleMap>
            )}
          </Box>

          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Click on the map or search for an address to select a location
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedAddress || !isLoaded}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
